import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import SwiperCore from "swiper";
import { Keyboard } from "swiper/modules";
import PortfolioMediaSlide from "./PortfolioMediaSlide.web";
import "swiper/css";

SwiperCore.use([Keyboard]);

interface PortfolioItem {
  id: number;
  url: string;
  type: "image" | "video" | "audio" | "application/pdf";
}

interface PortfolioCarouselOverlayProps {
  open: boolean;
  items: PortfolioItem[];
  currentActiveIndex: number;
  onClose: () => void;
}

const OverlayContainer = styled("div")({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100dvh",
  background: "radial-gradient(ellipse at center, #121212 0%, #000000 100%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1300,
});

const CloseButton = styled(IconButton)({
  position: "fixed",
  top: 16,
  right: 16,
  color: "#fff",
  zIndex: 1500,
});

const MediaWrapper = styled("div")({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 60px",
  boxSizing: "border-box",
  "@media (max-width: 600px)": {
    padding: "0px",
  },
});

const SlideContent = styled("div")({
  padding: "30px 0",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Media = styled("div")({
  width: "90%",
  height: "90%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const NavButton = styled(IconButton)({
  height: "35px",
  width: "35px",
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1400,
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  color: "#fff",
  backdropFilter: "blur(6px)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  "@media (max-width: 600px)": {
    display: "none",
  },
});

const PrevButton = styled(NavButton)({
  left: 20,
});

const NextButton = styled(NavButton)({
  right: 20,
});

const PortfolioCarousel: React.FC<PortfolioCarouselOverlayProps> = ({
  open,
  onClose,
  items,
  currentActiveIndex,
}) => {
  const scrollPosition = useRef(0);
  const swiperRef = useRef<SwiperType | null>(null);

  const [activeIndex, setActiveIndex] = useState(currentActiveIndex);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    scrollPosition.current = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollPosition.current}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollPosition.current);
    };
  }, [open]);

  if (!open) return null;

  return (
    <OverlayContainer onClick={onClose}>
      <CloseButton onClick={onClose}>
        <CloseRounded />
      </CloseButton>

      <MediaWrapper
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <PrevButton onClick={() => swiperRef.current?.swiper?.slidePrev()}>
          <ChevronLeftRounded />
        </PrevButton>

        <Swiper
          noSwiping={true}
          noSwipingClass="swiper-no-swiping"
          initialSlide={currentActiveIndex}
          keyboard={{ enabled: true }}
          modules={[Keyboard]}
          onSlideChange={(swiper: SwiperType) => {
            setActiveIndex(swiper.activeIndex);
          }}
          ref={swiperRef}
          style={{ width: "100%", height: "100%" }}
        >
          {items.map((item, index) => (
            <SwiperSlide key={index}>
              <SlideContent>
                <Media>
                  <PortfolioMediaSlide
                    item={item}
                    isMuted={isMuted}
                    isActive={index === activeIndex}
                    onToggleMute={() => setIsMuted((prev) => !prev)}
                  />
                </Media>
              </SlideContent>
            </SwiperSlide>
          ))}
        </Swiper>

        <NextButton onClick={() => swiperRef.current?.swiper?.slideNext()}>
          <ChevronRightRounded />
        </NextButton>
      </MediaWrapper>
    </OverlayContainer>
  );
};

export default PortfolioCarousel;
